import { cn } from 'cn';
import React, { type ReactElement } from 'react';
import Icon from '../../Icon';

interface PaginationPagesProps {
  children: ReactElement<unknown> | ReactElement<unknown>[];
  currentPage: number;
  pagesCount: number;
  visiblePagesCount: number;
}

export function PaginationPages({
  children,
  currentPage,
  pagesCount,
  visiblePagesCount,
}: PaginationPagesProps) {
  const pages = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return null;

    const element = child as React.ReactElement<
      React.HTMLAttributes<HTMLElement>
    >;
    const isCurrentPage = currentPage - 1 === index;

    return (
      <li key={index} className="text-neutral-110">
        {React.cloneElement(element, {
          'aria-current': isCurrentPage ? 'page' : undefined,
          ...(isCurrentPage
            ? { 'data-testid': 'pagination-current-page' }
            : {}),
          className: cn(
            'flex size-8 items-center justify-center rounded',
            'typ-button-xs active:bg-blue-10 active:text-blue-100 enabled:hover:bg-neutral-10',
            {
              'bg-blue-110 text-white enabled:hover:bg-blue-110 enabled:hover:text-neutral-110':
                isCurrentPage,
            },
            element.props.className,
          ),
        })}
      </li>
    );
  });

  if (pagesCount < visiblePagesCount) return pages;

  const visibleOnEachSide = Math.floor(visiblePagesCount / 2);
  const showMoreThanFirstPages =
    currentPage > visiblePagesCount - visibleOnEachSide;
  const showEndPages = currentPage > pagesCount - (visibleOnEachSide + 1);
  const [firstPage] = pages;
  const lastPage = pages[pagesCount - 1];
  const showDots = pagesCount > visiblePagesCount;
  const nearestPages = showEndPages
    ? pages.slice(-visiblePagesCount + 1)
    : pages.slice(
        currentPage - visibleOnEachSide,
        currentPage + visibleOnEachSide,
      );
  const dots = (
    <li key="dots" data-testid="dots">
      <div className="text-neutral-110 flex size-8 items-end justify-center pb-0.5">
        <Icon type="more" size="tiny" />
      </div>
    </li>
  );

  return (
    <>
      {showMoreThanFirstPages
        ? [firstPage, showDots ? dots : null, ...nearestPages].filter(Boolean)
        : pages.slice(0, visiblePagesCount)}
      {showDots && showEndPages === false ? [dots, lastPage] : null}
    </>
  );
}
